import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { useGoBack } from 'hooks/useGoBack';

import Loading from '@karehero/llama/components/pages/Loading/Loading';

import {
  useVerifyAccountMutation,
  useUpgradeAccountRoleMutation,
} from 'store/api/account';
import { newToast } from 'store/slices/uiSlice';
import { useGetAllAccountRoleQuery } from 'store/api/accountRole';
import { useGetAllOrganizationQuery } from 'store/api/organization';
import { useGetAllReferringPartnersWithOrganizationsQuery } from 'store/api/referringPartner';
import {
  VerifyAccountForm,
  type VerifyAccountPayload,
} from '@karehero/llama/pwa/organisms/VerifyAccountForm/VerifyAccountForm';

export const VerifyAccount = () => {
  const { accountID } = useParams();
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const [verifyAccount] = useVerifyAccountMutation();
  const [upgradeAccountRole] = useUpgradeAccountRoleMutation();
  const { data: accountRoles, isLoading: isAccountRolesLoading } =
    useGetAllAccountRoleQuery();
  // TODO[pagination]: replace with searchable autocomplete
  const { data: organizationsData, isLoading: isOrganizationsLoading } =
    useGetAllOrganizationQuery({ perPage: 1000 });
  const organizations = organizationsData?.data;
  const { data: partnersData, isLoading: isPartnersLoading } =
    useGetAllReferringPartnersWithOrganizationsQuery();

  const partners = partnersData?.map((p) => p.partner);

  const handleSubmit = useCallback(
    async (accountID: string, payload: VerifyAccountPayload) => {
      const isUpgrade =
        payload.roleName === 'Organization Owner' ||
        payload.roleName === 'Referring Partner Admin';

      let res;
      if (isUpgrade && payload.roleName) {
        res = await upgradeAccountRole({
          accountID,
          role: payload.roleName,
          organizationID: payload.organizationID,
          partnerID: payload.partnerID,
        });
      } else {
        res = await verifyAccount({
          accountID,
          organizationID: payload.organizationID || '',
          roleID: payload.roleID || '',
        });
      }

      if (!('data' in res)) {
        dispatch(
          newToast({
            title: 'Failed to update account role',
            variant: 'error',
          }),
        );
        return;
      }

      goBack();
    },
    [goBack, verifyAccount, upgradeAccountRole, dispatch],
  );

  if (isAccountRolesLoading || isOrganizationsLoading || isPartnersLoading) {
    return <Loading />;
  }

  return (
    <VerifyAccountForm
      accountID={accountID || ''}
      accountRoles={accountRoles || []}
      organizations={organizations || []}
      partners={partners || []}
      onBack={goBack}
      onSubmit={handleSubmit}
    />
  );
};

export default VerifyAccount;
